import * as THREE from "three";
import {Rectangle, PointConfiguration} from "./shapes";
import {KalmanPredictor} from "./kalman_predictor";

const MAX_POINTS = 150;
const POINT_CHANNELS = 3;
const MATERIAL = new THREE.LineBasicMaterial( { color: 0x000000 } );

export class DirtyRect {
    private _rect: Rectangle | null;

    constructor() {
        this._rect = null;
    }
    addPoint(p: PointConfiguration) {
        if(this._rect) {
            this._rect = this._rect.includePoint(p);
        } else {
            this._rect = Rectangle.fromPoint(p);
        }
    }
    reset() {
        this._rect = null;
    }
    get(): Rectangle | null {
        return this._rect!;
    }
}

export class Fragment {
    public dirtyRect: DirtyRect;

    private _line: THREE.Line;

    constructor() {
        let geometry = new THREE.BufferGeometry();
        let positions = new Float32Array(POINT_CHANNELS * MAX_POINTS);
        geometry.addAttribute("position", new THREE.BufferAttribute(positions, POINT_CHANNELS));
        geometry.setDrawRange(0, 0);

        this._line = new THREE.Line(geometry, MATERIAL);

        this.dirtyRect = new DirtyRect();
    }

    get el() {return this._line;}

    addPoint({x, y}: PointConfiguration) {
        // Add to array
        let g = this._line.geometry as THREE.BufferGeometry;
        let arr = g.attributes.position.array as Float32Array;
        let i = g.drawRange.count;
        console.assert(i < MAX_POINTS);
        arr[i*POINT_CHANNELS+0] = x;
        arr[i*POINT_CHANNELS+1] = y;
        arr[i*POINT_CHANNELS+2] = 0;

        // Update the bounding rect
        this.dirtyRect.addPoint({x, y});

        // Tell it about the new point
        g.setDrawRange(g.drawRange.start, g.drawRange.count+1);
        (g.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    }

    get lastPoint() {
        let g = this._line.geometry as THREE.BufferGeometry;
        let arr = g.attributes.position.array as Float32Array;
        let i = g.drawRange.count-1;
        let x = arr[i*POINT_CHANNELS+0];
        let y = arr[i*POINT_CHANNELS+1];
        return new THREE.Vector2(x, y);
    }

    get isFull() {
        let drawRange = (this._line.geometry as THREE.BufferGeometry).drawRange;
        return drawRange.count == MAX_POINTS
    }

    clear() {
        let g = this._line.geometry as THREE.BufferGeometry;
        g.setDrawRange(g.drawRange.start, 0);
        this.dirtyRect.reset();
        (g.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    }
}

export class Stroke {
    public dirtyRect: DirtyRect;

    private _fragment: Fragment;
    private _group: THREE.Group;
    
    constructor() {
        this._fragment = new Fragment();
        this._group = new THREE.Group();
        this._group.add(this._fragment.el);

        this.dirtyRect = new DirtyRect();
    }
    
    get el() {return this._group;}

    addPoint(p: PointConfiguration) {
        if(this._fragment.isFull) {
            let fragment = new Fragment();
            this._group.add(fragment.el);
            fragment.addPoint(this._fragment.lastPoint);
            this._fragment = fragment;
        }

        this._fragment.addPoint(p);
        this.dirtyRect.addPoint(p);
    }

    get lastPoint() {
        return this._fragment.lastPoint;
    }
}

export class StrokeGenerator {
    private _stroke: Stroke;
    private _predictor: KalmanPredictor;
    private _predictionLine: Fragment;
    private _group: THREE.Group;

    constructor() {
        // Stroke
        this._stroke = new Stroke();

        // Prediction
        this._predictionLine = new Fragment();
        this._predictor = new KalmanPredictor(10);
        this._predictor.reset();

        // Combination
        this._group = new THREE.Group();
        this._group.add(this._stroke.el);
        this._group.add(this._predictionLine.el);
    }

    get el() {
        return this._group;
    }

    down(p: PointConfiguration) {
        this._stroke.addPoint(p);
        this._predictor.addPoint(p);
    }
    move(p: PointConfiguration) {
        let lastPoint = this._stroke.lastPoint;
        let dirtyRect = Rectangle.fromPoint(lastPoint);
        
        // Add new point
        this._stroke.addPoint(p);
        this._predictor.addPoint(p);
        dirtyRect = dirtyRect.includePoint(p);

        // Refresh area for old prediction
        let oldPredictionRect = this._predictionLine.dirtyRect.get();
        if(oldPredictionRect) {
            dirtyRect = dirtyRect.union(oldPredictionRect);
        }
        this._stroke.dirtyRect.get();

        // Update prediction and include area
        this._updatePredictedStroke();
        dirtyRect = dirtyRect.union(this._predictionLine.dirtyRect.get()!);

        return dirtyRect;
    }

    _updatePredictedStroke() {
        this._predictionLine.clear();
        let ps = this._predictor.calculatePrediction();
        ps.forEach((p: PointConfiguration) => {
            this._predictionLine.addPoint(p);
        });
    }
    
    up() {
        this._predictionLine.clear();
    }
}
